import classNames from "classnames";
import { difference, forEach, join, map } from "lodash";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { CellRenderers } from "utils/tableUtils";

import ACTrigger from "ac-components/ACTrigger";
import ACButton from "ac-components/ac-button/ACButton";
import FilterIcon from "ac-components/ac-icons/FilterIcon";
import ACTag, { ACTagProps } from "ac-components/ac-tag/ACTag";

import "./ACResponsiveTags.less";

declare global {
  interface Window {
    dashboardPrinting: boolean;
    acReport: boolean;
  }
}

export default function ACResponsiveTags({
  tags,
  columnTitle,
  secondaryLabelDelimiter,
  secondaryTitle,
  onFilterClick,
  itemRenderer,
  tagProps = {},
  hidePopoverOnSingleItem = false,
}: {
  tags?: string[];
  columnTitle?: string;
  secondaryLabelDelimiter?: string;
  secondaryTitle?: string;
  onFilterClick?: (tag: string, index: number) => void;
  itemRenderer?: (tag: string) => ReactNode;
  tagProps?: Partial<ACTagProps>;
  hidePopoverOnSingleItem?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ref, inViewport] = useInView();
  const [forceRender, setForceRender] = useState(
    window.dashboardPrinting || false,
  );
  const [renderAllTags, setRenderAllTags] = useState(window.acReport || false);
  const itemsRef = useRef<{ [key: string]: DOMRect }>({});
  const prefixCls = "ac-responsive-tags";
  const [visibleTags, setVisibleTags] = useState(tags);
  const [popupVisible, setPopupVisible] = useState(false);

  const tagJson = JSON.stringify(tags || []);

  const setRefs = useCallback(
    (node) => {
      // Ref's from useRef needs to have the node assigned to `current`
      containerRef.current = node;
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      ref(node);
    },
    [ref],
  );

  useEffect(() => {
    setVisibleTags(JSON.parse(tagJson));
    const dashboardPrint = () => setForceRender(true);
    document.addEventListener("dashboardPrint", dashboardPrint);
    const renderAllTags = () => setRenderAllTags(true);
    document.addEventListener("acReport", renderAllTags);

    return () => {
      document.removeEventListener("dashboardPrint", dashboardPrint);
      document.removeEventListener("acReport", renderAllTags);
    };
  }, [tagJson]);

  useLayoutEffect(() => {
    const containerElement = containerRef.current;
    const tags = JSON.parse(tagJson);
    if (
      !containerElement ||
      !tags ||
      tags.length === 0 ||
      (!inViewport && !forceRender)
    ) {
      return;
    }
    const callback = () => {
      const threshold = 27;
      const containerBounds = containerElement.getBoundingClientRect();
      const visibleTags: string[] = [tags[0]];
      let left = itemsRef.current[tags[0]]?.width ?? 0 + 8;
      for (let i = 1; i < tags.length; i++) {
        const tag = tags[i];
        if (!itemsRef.current[tag]) {
          return;
        }
        const tagBounds = itemsRef.current[tag];
        if (left + tagBounds.width + threshold < containerBounds.width) {
          visibleTags.push(tag);
        } else {
          break;
        }
        left += tagBounds.width + 8; // 8px margin between tags
      }
      setVisibleTags((prev) => {
        if (
          difference(prev, visibleTags).length > 0 ||
          difference(visibleTags, prev || []).length > 0
        ) {
          return visibleTags;
        }
        return prev;
      });
    };

    const resizeObserver = new ResizeObserver(callback);

    resizeObserver.observe(containerElement);
    requestAnimationFrame(callback);

    return () => resizeObserver.disconnect();
  }, [tagJson, inViewport, forceRender]);

  const hiddenTags = difference(tags, visibleTags || []);

  const labelMap: { [key: string]: { primary: string; secondary: string } } =
    {};
  forEach(tags, (tag) => {
    labelMap[tag] = {
      primary: getPrimaryLabel(tag, secondaryLabelDelimiter),
      secondary: getSecondaryLabel(tag, secondaryLabelDelimiter),
    };
  });

  if (!tags || tags.length === 0) {
    return CellRenderers.VALUE_OR_NA();
  }

  if (renderAllTags) {
    return join(tags, ", ");
  }

  const popupContent = (
    <div
      className={classNames(
        `${prefixCls}-popup-container`,
        secondaryTitle ? "two-column" : "one-column",
        onFilterClick ? "has-filter" : "",
      )}
    >
      <div className="tag-column tag-column-1">
        {columnTitle ? (
          <div className="column-title white-space-nowrap">{columnTitle}</div>
        ) : null}
        {tags.map((t) => (
          <div className="tag-column-cell" key={t} title={labelMap[t].primary}>
            {itemRenderer
              ? itemRenderer(labelMap[t].primary)
              : labelMap[t].primary}
          </div>
        ))}
      </div>
      {secondaryTitle ? (
        <div className="tag-column tag-column-2">
          <div className="column-title white-space-nowrap">
            {secondaryTitle}
          </div>
          {tags.map((t) => (
            <div
              className="tag-column-cell"
              key={t}
              title={labelMap[t].secondary}
            >
              {labelMap[t].secondary || <span style={{ opacity: 0.5 }}>-</span>}
            </div>
          ))}
        </div>
      ) : null}
      {onFilterClick ? (
        <div className="tag-column-filter">
          {/* if columnTitle is available then render filter column title, else the layout distorts */}
          {columnTitle ? <div className="column-title">&nbsp;</div> : null}
          {tags.map((t, i) => (
            <div className="tag-column-cell" key={t}>
              <ACButton
                type="ghost"
                iconOnly
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  return onFilterClick(t, i);
                }}
              >
                <FilterIcon />
              </ACButton>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={`${prefixCls}-container`} ref={setRefs}>
      {!inViewport && !forceRender ? (
        <ACTag label="..." />
      ) : (
        <ACTrigger
          popup={popupContent}
          trigger={hidePopoverOnSingleItem && tags?.length === 1 ? "" : "hover"}
          placement={"bottomLeft"}
          popupVisible={popupVisible}
          onPopupVisibleChange={setPopupVisible}
          enterDelay={0.5}
        >
          <span className="d-inline-block w-100">
            {map(visibleTags, (t, i) => (
              <ACTag
                {...tagProps}
                label={t}
                key={t}
                ref={(d) => {
                  if (d) {
                    itemsRef.current[t] = d.getBoundingClientRect();
                  }
                }}
                secondaryLabelDelimiter={secondaryLabelDelimiter}
                onFilterClick={
                  onFilterClick ? () => onFilterClick(t, i) : undefined
                }
                className="text-ellipsis"
                style={{
                  maxWidth: `calc(100% - ${hiddenTags!.length > 0 ? 27 : 0}px)`,
                  display: "inline-block",
                  ...(tagProps.style ? tagProps.style : {}),
                }}
              >
                {itemRenderer ? itemRenderer(t) : undefined}
              </ACTag>
            ))}
            {hiddenTags.length ? (
              <span
                className={`${prefixCls}-hidden-tag-count text-small-highlight text-ellipsis`}
              >
                +{hiddenTags.length}
              </span>
            ) : null}
          </span>
        </ACTrigger>
      )}
    </div>
  );
}

const getPrimaryLabel = (tag: string, delimiter?: string) => {
  if (delimiter) {
    return tag.split(delimiter)[0];
  }
  return tag;
};
const getSecondaryLabel = (tag: string, delimiter?: string) => {
  if (delimiter && tag.includes(delimiter)) {
    return tag.split(delimiter).slice(1).join(delimiter);
  }
  return "";
};
